import "@/App.css";
import { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, homeForRole } from "@/context/AuthContext";
import { Logo, Panel, Btn, Input } from "@/components/kit";
import PortalSelection from "@/pages/PortalSelection";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import BusinessRegister from "@/pages/BusinessRegister";
import Verify from "@/pages/Verify";
import ResetPassword from "@/pages/ResetPassword";
import Legal from "@/pages/Legal";
import CustomerPortal from "@/pages/CustomerPortal";
import StaffPortal from "@/pages/StaffPortal";
import ReceptionPortal from "@/pages/ReceptionPortal";
import AdminPortal from "@/pages/AdminPortal";
import PlatformPortal from "@/pages/PlatformPortal";
import PaymentCallback from "@/pages/PaymentCallback";
import About from "@/pages/About";

const SITE_PASSWORD = process.env.REACT_APP_CONSTRUCTION_PASSWORD || "beetdrop2026";

function ConstructionGate({ children }) {
  const [ok, setOk] = useState(() => localStorage.getItem("bd_construction_ok") === "1");
  const [val, setVal] = useState("");
  if (ok) return children;
  const submit = (e) => {
    e.preventDefault();
    if (val === SITE_PASSWORD) { localStorage.setItem("bd_construction_ok", "1"); setOk(true); }
    else alert("Incorrect password");
  };
  return (
    <div className="bd-page flex flex-col items-center justify-center px-6 min-h-screen">
      <div className="mb-8"><Logo size={48} /></div>
      <Panel className="w-full max-w-sm p-8 text-center">
        <h1 className="font-head text-2xl font-bold text-white">Coming soon</h1>
        <p className="text-sm text-purple-300/70 mt-2 mb-6">BeetDrop is under construction. Enter the access password to preview the site.</p>
        <form onSubmit={submit} className="space-y-3">
          <Input data-testid="construction-password" type="password" placeholder="Access password" value={val} onChange={(e) => setVal(e.target.value)} autoFocus />
          <Btn type="submit" className="w-full" data-testid="construction-submit">Enter site</Btn>
        </form>
      </Panel>
    </div>
  );
}

function Loading() {
  return <div className="bd-page flex items-center justify-center min-h-screen"><div className="text-purple-300 animate-pulse font-head">Loading BeetDrop…</div></div>;
}

function Protected({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeForRole(user.role)} replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" theme="dark" toastOptions={{ style: { background: "#180735", border: "1px solid #451B7F", color: "#fff" } }} />
      <ConstructionGate>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/about" element={<About />} />
              <Route path="/login" element={<Login />} />
              <Route path="/business/register" element={<BusinessRegister />} />
              <Route path="/verify" element={<Verify />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/legal" element={<Navigate to="/legal/terms-of-service" replace />} />
              <Route path="/legal/:doc" element={<Legal />} />
              <Route path="/portals" element={<Protected><PortalSelection /></Protected>} />
              <Route path="/customer" element={<Protected roles={["guest", "admin"]}><CustomerPortal /></Protected>} />
              <Route path="/staff" element={<Protected roles={["staff", "manager", "admin"]}><StaffPortal /></Protected>} />
              <Route path="/kitchen" element={<Protected roles={["staff", "manager", "admin"]}><StaffPortal expectDept="Kitchen" /></Protected>} />
              <Route path="/food-beverage" element={<Protected roles={["staff", "manager", "admin"]}><StaffPortal expectDept="Food & Beverage" /></Protected>} />
              <Route path="/housekeeping" element={<Protected roles={["staff", "manager", "admin"]}><StaffPortal expectDept="Housekeeping" /></Protected>} />
              <Route path="/maintenance" element={<Protected roles={["staff", "manager", "admin"]}><StaffPortal expectDept="Maintenance" /></Protected>} />
              <Route path="/events" element={<Protected roles={["staff", "manager", "admin"]}><StaffPortal expectDept="Events" /></Protected>} />
              <Route path="/reception" element={<Protected roles={["reception", "admin"]}><ReceptionPortal /></Protected>} />
              <Route path="/admin" element={<Protected roles={["admin"]}><AdminPortal /></Protected>} />
              <Route path="/platform" element={<Protected roles={["superadmin"]}><PlatformPortal /></Protected>} />
              <Route path="/payment/callback" element={<Protected roles={["admin"]}><PaymentCallback /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </ConstructionGate>
    </div>
  );
}

export default App;
